/* QuickJournalEntry — single-modal flow for recording any non-Stripe transaction.
 * Hides the double-entry mechanics behind a friendly preset picker; bookkeepers
 * can expand "Advanced" to see and edit the underlying debit/credit lines.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  X, Save, Loader2, ChevronDown, ChevronUp, ArrowDownLeft, ArrowUpRight, BookOpen,
} from 'lucide-react';
import AccountCodePicker from './AccountCodePicker';
import FundPicker from './FundPicker';
import CampaignPicker from './CampaignPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Each preset maps to a backend source_type and steers the picker so users
// don't have to think in terms of account types.
const PRESETS = [
  { id: 'cash_donation',   label: 'Cash donation',     hint: 'Cash received in person and deposited.',
    accountFilter: 'revenue',  defaultCode: '4100', direction: 'inflow' },
  { id: 'check_donation',  label: 'Check donation',    hint: 'Paper check from a donor.',
    accountFilter: 'revenue',  defaultCode: '4100', direction: 'inflow' },
  { id: 'wire_transfer',   label: 'Wire / ACH',        hint: 'Bank transfer received.',
    accountFilter: 'revenue',  defaultCode: '4100', direction: 'inflow' },
  { id: 'grant',           label: 'Grant received',    hint: 'Foundation, government, or corporate grant.',
    accountFilter: 'revenue',  defaultCode: '4300', direction: 'inflow' },
  { id: 'sponsorship',     label: 'Sponsorship',       hint: 'Event or program sponsorship.',
    accountFilter: 'revenue',  defaultCode: '4230', direction: 'inflow' },
  { id: 'in_kind_donation', label: 'In-kind donation', hint: 'Non-cash gift (goods, services, pro bono time).',
    accountFilter: 'revenue',  defaultCode: '4140', direction: 'inflow' },
  { id: 'reimbursement',   label: 'Reimbursement paid', hint: 'Pay back a volunteer or staff for an expense.',
    accountFilter: 'expense',  defaultCode: '5700', direction: 'outflow' },
  { id: 'adjustment',      label: 'Other / Adjustment', hint: 'Anything else — bookkeeping correction, transfer.',
    accountFilter: null,       defaultCode: '',     direction: 'auto' },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

const QuickJournalEntry = ({ open, onClose, onSaved, defaultCashAccount = '1100' }) => {
  const [presetId, setPresetId] = useState('cash_donation');
  const [entryDate, setEntryDate] = useState(todayIso());
  const [memo, setMemo] = useState('');
  const [reference, setReference] = useState('');
  const [accountCode, setAccountCode] = useState('4100');
  const [amount, setAmount] = useState('');
  const [cashAccount, setCashAccount] = useState(defaultCashAccount);
  const [direction, setDirection] = useState('inflow');
  const [advanced, setAdvanced] = useState(false);
  const [fundId, setFundId] = useState(null);
  const [campaignId, setCampaignId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const preset = useMemo(() => PRESETS.find((p) => p.id === presetId), [presetId]);

  useEffect(() => {
    if (!preset) return;
    if (preset.defaultCode) setAccountCode(preset.defaultCode);
    setDirection(preset.direction === 'auto' ? 'inflow' : preset.direction);
  }, [preset]);

  useEffect(() => {
    if (open) {
      setPresetId('cash_donation');
      setEntryDate(todayIso());
      setMemo(''); setReference('');
      setAccountCode('4100'); setAmount('');
      setCashAccount(defaultCashAccount);
      setDirection('inflow');
      setFundId(null);
      setAdvanced(false);
      setError('');
    }
  }, [open, defaultCashAccount]);

  const numericAmount = parseFloat(amount) || 0;

  const save = async () => {
    setError('');
    if (!entryDate) return setError('Entry date is required.');
    if (!memo.trim()) return setError('Memo is required so this entry tells a story.');
    if (!accountCode) return setError('Pick an account code.');
    if (numericAmount <= 0) return setError('Amount must be greater than $0.');
    setSaving(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/journal-entries/quick`, {
        entry_date: entryDate,
        memo: memo.trim(),
        reference: reference.trim() || null,
        source_type: presetId,
        account_code: accountCode,
        amount: numericAmount,
        cash_account: cashAccount,
        direction,
        fund_id: fundId || null,
        campaign_id: campaignId || null,
      }, { withCredentials: true });
      onSaved?.(r.data);
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save entry.');
    }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="quick-journal-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-[#E5E7EB] sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#BA1826]" />
            <h2 className="font-heading text-lg font-bold text-[#0A0A0A]">Record a transaction</h2>
          </div>
          <button onClick={onClose} className="text-[#4B5563] hover:text-[#0A0A0A]" data-testid="quick-journal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-[#4B5563] mb-1.5">Transaction type</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="quick-journal-presets">
              {PRESETS.map((p) => (
                <button key={p.id} type="button" onClick={() => setPresetId(p.id)}
                  className={`text-left px-3 py-2 rounded border text-xs font-semibold transition-colors ${
                    presetId === p.id
                      ? 'border-[#BA1826] bg-[#FFF8F8] text-[#BA1826]'
                      : 'border-[#E5E7EB] bg-white text-[#0A0A0A] hover:border-[#BA1826]/40'
                  }`}
                  data-testid={`preset-${p.id}`}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    {p.direction === 'inflow'  && <ArrowDownLeft className="w-3 h-3 text-green-700" />}
                    {p.direction === 'outflow' && <ArrowUpRight  className="w-3 h-3 text-amber-700" />}
                    <span>{p.label}</span>
                  </div>
                  <p className="text-[10px] font-normal text-[#9CA3AF] leading-tight">{p.hint}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#4B5563] mb-1">Date *</label>
              <input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)}
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm" data-testid="quick-journal-date" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#4B5563] mb-1">Amount (USD) *</label>
              <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm font-mono" data-testid="quick-journal-amount" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#4B5563] mb-1">Memo *</label>
            <input value={memo} onChange={(e) => setMemo(e.target.value)}
              placeholder="What is this for? (e.g., 'February Garcia family donation')"
              className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm" data-testid="quick-journal-memo" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#4B5563] mb-1">Reference (optional)</label>
            <input value={reference} onChange={(e) => setReference(e.target.value)}
              placeholder="Check #, wire confirmation, grant award letter…"
              className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm" data-testid="quick-journal-reference" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#4B5563] mb-1">
              {direction === 'inflow' ? 'Revenue account (income)' : 'Expense account (outflow)'}
              <span className="text-[#BA1826] ml-0.5">*</span>
            </label>
            <AccountCodePicker
              value={accountCode}
              onChange={setAccountCode}
              typeFilter={direction === 'inflow' ? 'revenue' : (direction === 'outflow' ? 'expense' : null)}
              ariaLabel="Account code"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#4B5563] mb-1">
              Fund designation
              <span className="ml-1 text-[10px] text-[#9CA3AF] font-normal">
                (optional — for donor-restricted gifts)
              </span>
            </label>
            <FundPicker value={fundId} onChange={setFundId} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#4B5563] mb-1">
              Campaign / Program
              <span className="ml-1 text-[10px] text-[#9CA3AF] font-normal">
                (optional — Kickoff Fundraiser, Growing Together, etc.)
              </span>
            </label>
            <CampaignPicker value={campaignId} onChange={setCampaignId} />
          </div>

          <button type="button" onClick={() => setAdvanced((v) => !v)}
            className="text-[11px] text-[#4B5563] inline-flex items-center gap-1 hover:text-[#0A0A0A]"
            data-testid="quick-journal-advanced-toggle">
            {advanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Advanced (cash account + direction)
          </button>

          {advanced && (
            <div className="grid grid-cols-2 gap-3 p-3 bg-[#FAFBFC] border border-[#E5E7EB] rounded">
              <div>
                <label className="block text-[11px] font-semibold text-[#4B5563] mb-1">Cash account</label>
                <AccountCodePicker
                  value={cashAccount}
                  onChange={setCashAccount}
                  typeFilter="asset"
                  ariaLabel="Cash account"
                />
                <p className="mt-1 text-[10px] text-[#9CA3AF]">Defaults to 1100 Operating Cash — Checking.</p>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[#4B5563] mb-1">Direction</label>
                <select value={direction} onChange={(e) => setDirection(e.target.value)}
                  className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded text-xs" data-testid="quick-journal-direction">
                  <option value="inflow">Inflow (money in)</option>
                  <option value="outflow">Outflow (money out)</option>
                </select>
              </div>
            </div>
          )}

          {/* Live double-entry preview — quietly educates without overwhelming */}
          {numericAmount > 0 && accountCode && (
            <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded p-2.5 text-[11px]"
              data-testid="quick-journal-preview">
              <p className="font-bold text-[#4B5563] mb-1">Will record:</p>
              <div className="font-mono text-[#0A0A0A] space-y-0.5">
                {direction === 'inflow' ? (
                  <>
                    <p>Debit <span className="font-bold">{cashAccount}</span> ${numericAmount.toFixed(2)}</p>
                    <p>Credit <span className="font-bold">{accountCode}</span> ${numericAmount.toFixed(2)}</p>
                  </>
                ) : (
                  <>
                    <p>Debit <span className="font-bold">{accountCode}</span> ${numericAmount.toFixed(2)}</p>
                    <p>Credit <span className="font-bold">{cashAccount}</span> ${numericAmount.toFixed(2)}</p>
                  </>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded" data-testid="quick-journal-error">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-[#E5E7EB] sticky bottom-0 bg-white">
          <button onClick={onClose} className="text-xs px-4 py-2 border border-[#E5E7EB] rounded text-[#4B5563] hover:bg-[#F8F9FA]">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            className="text-xs px-4 py-2 bg-[#BA1826] text-white rounded font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="quick-journal-save">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Record entry
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickJournalEntry;
